import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
import {
  LAYOUT_PRIORITY,
  MarkerLayoutClient,
  type LayoutContext,
  type LayoutItem,
  type MarkerLayoutRegistry,
} from './markerLayout';

export interface NewsMarker {
  time: number; // timestamp in ms
  headline: string; // headline text for tooltip
  isBreaking: boolean; // breaking news gets special styling
}

interface NewsMarkerRendererData {
  x: number;
  headline: string;
  isBreaking: boolean;
}

class NewsMarkerPaneRenderer implements IPrimitivePaneRenderer {
  private _data: NewsMarkerRendererData[] = [];
  private _fontSize: number = 14;
  private _bottomOffset: number = 45;

  update(data: NewsMarkerRendererData[], fontSize: number, bottomOffset: number): void {
    this._data = data;
    this._fontSize = fontSize;
    this._bottomOffset = bottomOffset;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const pixelRatio = scope.horizontalPixelRatio as number;
      const scaledFontSize = this._fontSize * pixelRatio;
      const bitmapHeight = scope.bitmapSize.height as number;

      ctx.font = `${scaledFontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Position markers just above the volume area (bottom of chart)
      const y = bitmapHeight - (this._bottomOffset * pixelRatio);

      for (const marker of this._data) {
        const x = marker.x * pixelRatio;

        // Draw lightning bolt emoji for breaking news
        if (marker.isBreaking) {
          ctx.fillText('⚡', x, y);
        } else {
          // Blue newspaper for regular high importance news
          ctx.fillText('📰', x, y);
        }
      }
    });
  }
}

class NewsMarkerPaneView implements IPrimitivePaneView {
  private _source: NewsMarkerPrimitive;
  private _renderer: NewsMarkerPaneRenderer;

  constructor(source: NewsMarkerPrimitive) {
    this._source = source;
    this._renderer = new NewsMarkerPaneRenderer();
  }

  zOrder(): PrimitivePaneViewZOrder {
    return 'top';
  }

  renderer(): IPrimitivePaneRenderer {
    const data = this._source.getRendererData();
    this._renderer.update(data, this._source.getFontSize(), this._source.getBottomOffset());
    return this._renderer;
  }
}

export class NewsMarkerPrimitive implements ISeriesPrimitive<Time> {
  private _markers: NewsMarker[] = [];
  private _paneView: NewsMarkerPaneView;
  private _requestUpdate?: () => void;
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _fontSize: number = 16;
  private _bottomOffset: number = 45;
  private _rendererData: NewsMarkerRendererData[] = [];
  /**
   * Shared marker layout: the ⚡/📰 lane at the pane bottom is FIXED; candle
   * markers flow around it, and two that would overlap fold into a "+N" chip.
   */
  private _layout = new MarkerLayoutClient('news', (ctx) => this._layoutItems(ctx));

  constructor() {
    this._paneView = new NewsMarkerPaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
    this._layout.setAttached(true);
  }

  detached(): void {
    this._layout.setAttached(false);
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  /** Join the chart's shared marker layout (null: every marker draws). */
  setMarkerLayout(reg: MarkerLayoutRegistry | null): void {
    this._layout.setRegistry(reg);
  }

  private _layoutItems(ctx: LayoutContext): LayoutItem[] {
    if (!(ctx.paneHeight > 0)) return [];
    const out: LayoutItem[] = [];
    const y = ctx.paneHeight - this._bottomOffset;
    const size = Math.ceil(this._fontSize * 1.25);
    this._markers.forEach((marker, i) => {
      const t = Math.floor(marker.time / 1000);
      if (t < ctx.fromTime || t > ctx.toTime) return;
      const x = ctx.timeToX(t);
      if (x === null) return;
      out.push({
        key: `${marker.time}:${i}`,
        time: t,
        x,
        side: 'above',
        anchorY: y,
        w: size,
        h: size,
        priority: LAYOUT_PRIORITY.fixed,
        fixed: true,
        label: `${marker.isBreaking ? '⚡' : '📰'} ${marker.headline}`,
        ref: marker,
      });
    });
    return out;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {
    // Called when chart needs to update views
  }

  setMarkers(markers: NewsMarker[]): void {
    this._markers = markers;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  setFontSize(size: number): void {
    this._fontSize = size;
    this._requestUpdate?.();
  }

  setBottomOffset(offset: number): void {
    this._bottomOffset = offset;
    this._requestUpdate?.();
  }

  getFontSize(): number {
    return this._fontSize;
  }

  getBottomOffset(): number {
    return this._bottomOffset;
  }

  /**
   * Check if a click at (x, y) hits any marker
   * Returns the marker if hit, null otherwise
   */
  checkClick(clickX: number, clickY: number, chartHeight: number): NewsMarker | null {
    if (!this._chart) {
      return null;
    }

    const hitRadius = 40; // pixels - generous hit area for Y offset variance
    const markerY = chartHeight - this._bottomOffset;
    const timeScale = this._chart.timeScale();

    for (const marker of this._markers) {
      // Calculate x position fresh for accurate hit detection
      const timeInSeconds = Math.floor(marker.time / 1000) as Time;
      const x = timeScale.timeToCoordinate(timeInSeconds);
      if (x === null) {
        continue;
      }

      const dx = clickX - x;
      const dy = clickY - markerY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance <= hitRadius) {
        return marker;
      }
    }

    return null;
  }

  getRendererData(): NewsMarkerRendererData[] {
    if (!this._chart) return [];

    const timeScale = this._chart.timeScale();
    const data: NewsMarkerRendererData[] = [];
    const shared = this._layout.placed();
    const kept = shared ? new Set(shared.map((p) => p.item.ref as NewsMarker)) : null;

    for (const marker of this._markers) {
      if (kept && !kept.has(marker)) continue;
      // Convert timestamp to Time format (seconds)
      const timeInSeconds = Math.floor(marker.time / 1000) as Time;

      // Get x coordinate from time
      const x = timeScale.timeToCoordinate(timeInSeconds);
      if (x === null) continue;

      data.push({
        x,
        headline: marker.headline,
        isBreaking: marker.isBreaking,
      });
    }

    // Cache for hit testing
    this._rendererData = data;

    return data;
  }
}
